// ./src/utils/api/CarTotalRevenueService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Car } from "./CarService";
import { RevenueKind } from "./AppEssentialsService";

// GraphQL queries
const CAR_TOTAL_REVENUE_LIST_QUERY = gql`
  query CarTotalRevenueList(
    $filter: CarTotalRevenueFilter
    $params: PaginationAndSorting
  ) {
    carTotalRevenueList(filter: $filter, params: $params) {
      code
      data {
        carId
        homeCurrency
        revenueKindId
        recordsCount
        amount
        car {
          id
          label
          make
          model
          color
        }
        revenueKind {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CAR_TOTAL_REVENUE_GET_QUERY = gql`
  query CarTotalRevenueGet($carId: ID, $homeCurrency: String, $revenueKindId: Int) {
    carTotalRevenueGet(carId: $carId, homeCurrency: $homeCurrency, revenueKindId: $revenueKindId) {
      code
      data {
        carId
        homeCurrency
        revenueKindId
        recordsCount
        amount
        car {
          id
          label
          make
          model
          color
        }
        revenueKind {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface CarTotalRevenue {
  carId?: string | undefined;
  homeCurrency?: string | undefined;
  revenueKindId?: number | undefined;
  recordsCount?: number | undefined;
  amount?: number | undefined;
  // Relations
  car?: Car | undefined;
  revenueKind?: RevenueKind | undefined;
}

export interface CarTotalRevenueFilter {
  carId?: string[] | null | undefined;
  homeCurrency?: string[] | null | undefined;
  revenueKindId?: number[] | null | undefined;
}

export interface CarTotalRevenueWhereInput {
  carId?: string | undefined;
  homeCurrency?: string | undefined;
  revenueKindId?: number | undefined;
}

/**
 * CarTotalRevenue service that handles car total revenue-related API calls.
 * This is a read-only service for viewing aggregated lifetime revenue statistics
 * per car, currency, and revenue kind.
 * Supports: list, get (composite key)
 */
export class CarTotalRevenueService {
  /**
   * Get a list of car total revenues with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: CarTotalRevenueFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CAR_TOTAL_REVENUE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single car total revenue by composite key (carId + homeCurrency + revenueKindId)
   */
  static async get(
    carId: string,
    homeCurrency: string,
    revenueKindId: number
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CAR_TOTAL_REVENUE_GET_QUERY, {
      carId,
      homeCurrency,
      revenueKindId,
    });
    return result;
  }
}